import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { ClompApi } from '../api/ClompApi';
import { getVolume, isMuted, playAchievement, playGoldenAppear, playGoldenCatch, playMugClick, setMuted, setVolume } from './clickerSounds';
import { ClompyMug, CoffeeMachine, ColdBrewTower, DiamondIcon, machineTier } from './ClickerArt';
import './ClompyClicker.css';

const api = new ClompApi();

// How often clicks are sent to the backend while the game is being played
const SYNC_MS = 2000;
// With no clicks to send, time played is still sent once this many seconds have built up
const IDLE_SYNC_SECONDS = 15;

// The golden diamond: how long after the game opens the first can appear, how long between later ones, and how long
// one stays before it gets away (all in seconds of the game being on screen; each wait is picked between two ends).
// The backend pays for one a minute at most, so the gaps stay above that.
const GOLDEN_FIRST = [45, 90];
const GOLDEN_GAP = [75, 150];
const GOLDEN_STAYS = 10;
// What one pays a guest, whose diamonds aren't saved; a logged-in player's is worked out by the backend
const GOLDEN_GUEST_REWARD = 25;
// How close the middle of a golden diamond may come to the mug, in pixels: half its own size at its largest as it
// bobs, and a gap
const GOLDEN_CLEAR_OF_MUG = 38;

// How long the banner for a newly earned achievement stays up, and how many can be earned at once before a single
// banner counts them instead of each getting its own
const BANNER_MS = 5000;
const BANNERS_AT_ONCE = 3;

// Coming back to the tab after it was out of sight this long asks the backend what was earned meanwhile (the backend
// decides for itself whether the player was really away)
const AWAY_CHECK_MS = 60 * 1000;

// What a player has before buying anything, and all a guest ever has
const STARTING_GAME = {
    levels: { sips: 0, machine: 0, barista: 0, thermos: 0 },
    owned: ['mug_classic', 'bg_night', 'fx_plus'],
    equipped: { mug: 'mug_classic', background: 'bg_night', effect: 'fx_plus' },
    perClick: 1,
    perSecond: 0,
    awayPerHour: 0,
    awayMaxSeconds: 3600
};

// The mark each click effect puts before the number that floats up from a click
const EFFECTS = {
    fx_plus: '',
    fx_stars: '★ ',
    fx_hearts: '♥ '
};

function between([low, high]) {
    return low + Math.floor(Math.random() * (high - low + 1));
}

// Seconds as a rough length of time, for the welcome back panel: 45 seconds, 12 minutes, 3 hours 20 minutes, 2 days 4 hours
function formatAway(seconds) {
    let some = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    if (minutes < 1) {
        return some(seconds, 'second');
    }
    if (hours < 1) {
        return some(minutes, 'minute');
    }
    if (hours < 24) {
        return some(hours, 'hour') + (minutes % 60 ? ` ${some(minutes % 60, 'minute')}` : '');
    }
    return some(Math.floor(hours / 24), 'day') + (hours % 24 ? ` ${some(hours % 24, 'hour')}` : '');
}

// Seconds as a length of time: 42s, 5m 07s, 2h 05m 07s
function formatDuration(seconds) {
    let pad = (n) => (n < 10 ? '0' : '') + n;
    let s = seconds % 60;
    let m = Math.floor(seconds / 60) % 60;
    let h = Math.floor(seconds / 3600);
    return h > 0 ? `${h.toLocaleString()}h ${pad(m)}m ${pad(s)}s` : m > 0 ? `${m}m ${pad(s)}s` : `${s}s`;
}

// The picture on the sound button: a speaker with sound coming out of it, and when `muted` a red circle with a line
// through it over the top
function SoundIcon({ muted }) {
    return (
        <svg viewBox="0 0 32 32" aria-hidden="true">
            <path d="M5 12h5l7-6v20l-7-6H5z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M21 12a6 6 0 0 1 0 8M24 8.5a11 11 0 0 1 0 15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            {muted ?
                <>
                    <circle cx="16" cy="16" r="13" fill="none" stroke="#ff3b30" strokeWidth="3" />
                    <path d="M6.8 6.8l18.4 18.4" fill="none" stroke="#ff3b30" strokeWidth="3" strokeLinecap="round" />
                </>
                : null}
        </svg>
    );
}

// A trophy cup, for achievements
function TrophyIcon() {
    return (
        <svg className="clompy-trophy" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M7 3h10v6a5 5 0 0 1-10 0z" />
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M7 5H4v2a3 3 0 0 0 3 3M17 5h3v2a3 3 0 0 1-3 3" />
            <rect fill="currentColor" x="11" y="13" width="2" height="5" />
            <rect fill="currentColor" x="8" y="18" width="8" height="3" rx="1" />
        </svg>
    );
}

// What stands on the counter beside the mug for an upgrade the player owns: its picture, under a tag with its
// level and what it earns, so neither needs the shop opening. `side` is 'left' or 'right' of the mug; `when` says
// when it earns, if that isn't all the time.
function Prop({ side, name, level, earns, when, children }) {
    return (
        <div className={`clompy-prop clompy-prop-${side}`} role="img" aria-label={`${name}, level ${level}: ${earns} diamonds${when ? ` ${when}` : ''}`}>
            <div className="clompy-prop-tag" aria-hidden="true">
                <span className="clompy-prop-level">Lv {level}</span>
                <span className="clompy-prop-earns"><DiamondIcon />{earns}</span>
                {when ? <span className="clompy-prop-when">{when}</span> : null}
            </div>
            {children}
        </div>
    );
}

// A panel shown over the game (the shop, the stats). Closes on its button, a click outside it or Escape.
// A `wide` panel (the shop) holds more than fits on screen, so it opens scrolled to its top: the keyboard's focus
// goes to the panel itself rather than to the button at its foot, which would scroll down to show it.
function Panel({ title, wide, closeLabel, onClose, children }) {
    let overlay = React.useRef(null);
    let body = React.useRef(null);
    React.useEffect(() => {
        let onKeyDown = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);
    React.useEffect(() => {
        if (wide) {
            body.current.focus({ preventScroll: true });
            overlay.current.scrollTop = 0;
        }
    }, [wide]);
    return (
        <div className="clompy-panel" ref={overlay} role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`clompy-panel-body${wide ? ' is-wide' : ''}`} ref={body} tabIndex="-1">
                <h2>{title}</h2>
                {children}
                <button type="button" className="clompy-button" onClick={onClose} autoFocus={!wide}>{closeLabel || 'Close'}</button>
            </div>
        </div>
    );
}

export class ClompyClickerGame extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showHelp: false,
            showShare: false,
            // A logged-in player's numbers as saved on the backend: diamonds they have to spend (which the backend
            // calls coins), diamonds ever earned, clicks ever made and seconds played. What is shown is these plus
            // what the clicks and seconds not yet sent will earn. A guest's count this visit only.
            saved: { coins: 0, allTime: 0, clicks: 0, seconds: 0, awaySeconds: 0, goldenCaught: 0 },
            // What the player has bought in the shop, and what that makes a click and a second worth
            game: STARTING_GAME,
            // What the shop sells: { upgrades, slots, cosmetics }, once loaded
            shop: null,
            // the id of the item being bought or put on, and the last refusal from the shop
            buying: null,
            shopError: null,
            pending: 0,
            pendingSeconds: 0,
            // 'shop' or 'stats' while that panel is open
            panel: null,
            // what floats up from recent clicks: [{ id, x, y, text, big }], placed as percentages of the scene
            pops: [],
            // the ids of the achievements the player has earned, and the banners waiting to announce new ones
            // ([{ key, title, description }]; the first is the one on screen)
            achievements: [],
            banners: [],
            // the golden diamond while one is on screen: { x, y } as percentages of the scene
            golden: null,
            // what the Cold Brew Tower earned while the player was away, until they have seen it: { seconds, counted, earned }
            away: null,
            pressed: false,
            // the game's sounds are switched off (remembered between visits)
            muted: isMuted(),
            // how loud they are, from 0 to 1 (also remembered)
            volume: getVolume(),
            // Logged-in players' diamonds are saved and count towards the leaderboard; anyone else plays for fun
            loggedIn: localStorage.getItem('token') !== null,
        }
        // clicks made, and seconds played, that a save under way is carrying
        this.sending = 0;
        this.sendingSeconds = 0;
        this.savePromise = Promise.resolve();
        this.nextPop = 0;
        this.nextBanner = 0;
        // seconds until the next golden diamond appears, or until the one on screen gets away
        this.goldenWait = between(GOLDEN_FIRST);
        this.stage = React.createRef();
    }

    componentDidMount() {
        if (this.state.loggedIn) {
            this.collectAway();
        }
        api.games.getClickerShop().then((shop) => {
            if (!this.unmounted) {
                this.setState({ shop });
            }
        }).catch((err) => console.log('Error loading the shop: ', err));
        this.timer = setInterval(this.sync, SYNC_MS);
        this.clock = setInterval(this.tick, 1000);
        document.addEventListener('visibilitychange', this.visibilityChanged);
    }

    componentWillUnmount() {
        clearTimeout(this.bannerTimer);
        clearInterval(this.timer);
        clearInterval(this.clock);
        document.removeEventListener('visibilitychange', this.visibilityChanged);
        this.unmounted = true;
        this.save(true);
    }

    // Load the player's game, collecting whatever their Cold Brew Tower earned since they were last here. Called when the
    // game opens and when it comes back into view after a while.
    collectAway() {
        api.games.collectClickerAway().then((mine) => {
            this.applyGame(mine);
            if (!this.unmounted && mine.away && mine.away.earned > 0) {
                this.setState({ away: mine.away });
            }
        }).catch((err) => this.syncFailed(err));
    }

    // Leaving the tab saves what is unsent; coming back after a while collects what was earned meanwhile
    visibilityChanged = () => {
        if (document.hidden) {
            this.hiddenAt = Date.now();
            this.save(true);
        } else if (this.hiddenAt && Date.now() - this.hiddenAt >= AWAY_CHECK_MS && this.state.loggedIn) {
            this.hiddenAt = null;
            // anything still unsent goes first, so its time isn't taken for time away
            this.save(true).then(() => !this.unmounted && this.collectAway());
        }
    }

    // Take the player's game from a backend answer: their numbers and their shop purchases
    applyGame(answer) {
        if (this.unmounted) {
            return;
        }
        this.setState({
            saved: {
                coins: answer.coins, allTime: answer.allTime, clicks: answer.clicks, seconds: answer.seconds,
                awaySeconds: answer.awaySeconds || 0, goldenCaught: answer.goldenCaught || 0
            },
            game: {
                levels: answer.levels, owned: answer.owned, equipped: answer.equipped, perClick: answer.perClick, perSecond: answer.perSecond,
                awayPerHour: answer.awayPerHour, awayMaxSeconds: answer.awayMaxSeconds
            },
            achievements: answer.achievements || []
        });
        if (answer.unlocked && answer.unlocked.length > 0) {
            this.announce(answer.unlocked);
        }
    }

    // Achievements have just been earned (their ids): play the sound and put up a banner for each, one after the
    // other. A crowd of them at once (as on the first visit after achievements were added) gets one banner between them.
    announce(ids) {
        let known = (this.state.shop && this.state.shop.achievements) || [];
        let earned = ids.map((id) => known.find((achievement) => achievement.id === id) || { id, title: 'Achievement unlocked', description: '' });
        let banners = earned.length > BANNERS_AT_ONCE ?
            [{ title: `${earned.length} achievements unlocked`, description: 'See them all under Stats.' }]
            : earned.map((achievement) => ({ title: achievement.title, description: achievement.description }));
        if (!this.state.muted) {
            playAchievement();
        }
        this.setState((s) => ({ banners: [...s.banners, ...banners.map((banner) => ({ ...banner, key: this.nextBanner++ }))] }), this.runBanners);
    }

    // Keep the first banner up for its time, then move on to the next
    runBanners = () => {
        if (this.bannerTimer || this.state.banners.length === 0 || this.unmounted) {
            return;
        }
        this.bannerTimer = setTimeout(() => {
            this.bannerTimer = null;
            if (!this.unmounted) {
                this.setState((s) => ({ banners: s.banners.slice(1) }), this.runBanners);
            }
        }, BANNER_MS);
    }

    // Where the next golden diamond appears, as percentages of the scene: somewhere in the sky, clear of the count at
    // the top and the counter at the bottom, and to the side of the mug or above it, never over it. A click meant
    // for the mug can't catch one, then: the player has to go and get it.
    goldenSpot() {
        let spot = () => ({ x: 10 + Math.random() * 80, y: 22 + Math.random() * 40 });
        let stage = this.stage.current;
        let mug = stage && stage.querySelector('.clompy-mug');
        if (!mug) {
            return spot();
        }
        let scene = stage.getBoundingClientRect();
        let box = mug.getBoundingClientRect();
        let left = (box.left - GOLDEN_CLEAR_OF_MUG - scene.left) / scene.width * 100;
        let right = (box.right + GOLDEN_CLEAR_OF_MUG - scene.left) / scene.width * 100;
        let top = (box.top - GOLDEN_CLEAR_OF_MUG - scene.top) / scene.height * 100;
        for (let tries = 0; tries < 50; tries++) {
            let at = spot();
            if (at.x < left || at.x > right || at.y < top) {
                return at;
            }
        }
        // a scene too narrow to have room beside the mug: as far to one side as a diamond goes
        return { ...spot(), x: Math.random() < 0.5 ? 10 : 90 };
    }

    // Once a second, while the game is on screen: time played counts up, and the golden diamond comes and goes
    tick = () => {
        if (document.hidden) {
            return;
        }
        this.goldenWait--;
        let golden = this.state.golden;
        if (this.goldenWait <= 0) {
            if (golden) {
                golden = null;
                this.goldenWait = between(GOLDEN_GAP);
            } else {
                golden = this.goldenSpot();
                this.goldenWait = GOLDEN_STAYS;
                if (!this.state.muted) {
                    playGoldenAppear();
                }
            }
        }
        this.setState((s) => ({ pendingSeconds: s.pendingSeconds + 1, golden }));
    }

    sync = () => {
        this.save(false);
    }

    // Send the clicks made and the time played since the last save. Time alone is sent once enough has built up,
    // when the game is being left, or with `now`. Resolves when the save is over, whether or not it worked.
    save(now) {
        let clicks = this.state.pending;
        let seconds = this.state.pendingSeconds;
        let due = now || this.unmounted || document.hidden || seconds >= IDLE_SYNC_SECONDS;
        if (!this.state.loggedIn || this.saving || (clicks === 0 && (seconds === 0 || !due))) {
            return this.savePromise;
        }
        this.saving = true;
        this.sending = clicks;
        this.sendingSeconds = seconds;
        if (!this.unmounted) {
            this.setState({ pending: 0, pendingSeconds: 0 });
        }
        this.savePromise = api.games.addClickerClicks(clicks, seconds).then((result) => {
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            this.applyGame(result);
        }).catch((err) => {
            // keep the clicks and the time to try again
            let unsent = this.sending;
            let unsentSeconds = this.sendingSeconds;
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            if (!this.unmounted) {
                this.setState((s) => ({ pending: s.pending + unsent, pendingSeconds: s.pendingSeconds + unsentSeconds }));
                this.syncFailed(err);
            }
        });
        return this.savePromise;
    }

    syncFailed(err) {
        if (err.response && err.response.status === 401) {
            // the login ran out: carry on as a guest with the numbers on screen
            this.setState({ loggedIn: false });
        } else {
            console.log('Error saving diamonds: ', err);
        }
    }

    // Buy something from the shop, or put on a cosmetic already owned. Clicks not yet saved are sent first, so
    // they are paid at what a click was worth when they were made.
    shopAction(item, action) {
        if (this.state.buying || !this.state.loggedIn) {
            return;
        }
        this.setState({ buying: item, shopError: null });
        this.savePromise.then(() => this.save(true)).then(() => action(item)).then((result) => {
            this.applyGame(result);
            this.setState({ buying: null });
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ buying: null, shopError: message || "The shop couldn't do that. Please try again." });
            if (err.response && err.response.status === 401) {
                this.setState({ loggedIn: false, shopError: 'Your login has expired. Log in again to use the shop.' });
            }
        });
    }

    buy = (item) => this.shopAction(item, (id) => api.games.buyClickerItem(id));

    equip = (item) => this.shopAction(item, (id) => api.games.equipClickerItem(id));

    // The golden diamond was clicked before it got away
    catchGolden = () => {
        let at = this.state.golden;
        if (!at) {
            return;
        }
        this.goldenWait = between(GOLDEN_GAP);
        this.setState({ golden: null });
        if (!this.state.muted) {
            playGoldenCatch();
        }
        if (!this.state.loggedIn) {
            this.addPop(at.x, at.y, GOLDEN_GUEST_REWARD, true);
            this.setState((s) => ({ saved: { ...s.saved, coins: s.saved.coins + GOLDEN_GUEST_REWARD, allTime: s.saved.allTime + GOLDEN_GUEST_REWARD } }));
            return;
        }
        api.games.claimClickerGolden().then((result) => {
            this.applyGame(result);
            if (!this.unmounted) {
                this.addPop(at.x, at.y, result.reward, true);
            }
        }).catch((err) => {
            // refused (one was claimed too recently, perhaps in another tab): it simply got away
            if (!err.response || err.response.status !== 409) {
                this.syncFailed(err);
            }
        });
    }

    // Float a number up from x, y (percentages of the scene), in the click effect being worn
    addPop(x, y, amount, big) {
        let mark = EFFECTS[this.state.game.equipped.effect] || '';
        let pop = { id: this.nextPop++, x, y, text: `${mark}+${amount.toLocaleString()}`, big: Boolean(big) };
        // only the latest few are kept, in case their animations never finish (as with reduced motion)
        this.setState((s) => ({ pops: [...s.pops.slice(-11), pop] }));
    }

    popDone = (id) => {
        this.setState((s) => ({ pops: s.pops.filter((pop) => pop.id !== id) }));
    }

    // Count a click on the mug, with its diamonds floating up from x, y (percentages of the scene)
    click(x, y) {
        this.setState((s) => ({ pending: s.pending + 1, pressed: true }));
        this.addPop(x, y, this.state.game.perClick);
        if (!this.state.muted) {
            playMugClick();
        }
    }

    clompyClicked = (e) => {
        // only the main button of a mouse; touches and pens always count
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }
        // the number rises from where the mug was clicked
        let box = this.stage.current.getBoundingClientRect();
        this.click((e.clientX - box.left) / box.width * 100, (e.clientY - box.top) / box.height * 100);
    }

    clompyReleased = () => {
        if (this.state.pressed) {
            this.setState({ pressed: false });
        }
    }

    // Space and Enter click the mug too
    clompyKeyDown = (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            this.click(40 + Math.random() * 20, 42);
        }
    }

    toggleSound = () => {
        let muted = !this.state.muted;
        setMuted(muted);
        this.setState({ muted });
    }

    // The volume slider was moved. Moving it turns the sound back on if it was muted.
    volumeChanged = (e) => {
        let volume = Number(e.target.value) / 100;
        setVolume(volume);
        setMuted(false);
        this.setState({ volume, muted: false });
    }

    // The slider was let go of: play a click, so the new volume can be heard
    volumeChosen = () => {
        if (!this.state.muted) {
            playMugClick();
        }
    }

    closeAway = () => {
        this.setState({ away: null });
    }

    closePanel = () => {
        this.setState({ panel: null, shopError: null });
    }

    getShareURL() {
        return window.location.origin + window.location.pathname;
    }

    toggleHelp() {
        this.setState({
            showHelp: !this.state.showHelp
        });
    }

    toggleShare() {
        this.setState({
            showShare: !this.state.showShare
        });
    }

    // A small picture of a cosmetic, for its tile in the shop
    renderPreview(cosmetic) {
        if (cosmetic.slot === 'mug') {
            return <ClompyMug skin={cosmetic.id} still className="clompy-preview-mug" aria-hidden="true" />;
        }
        if (cosmetic.slot === 'background') {
            return <span className={`clompy-preview-bg clompy-${cosmetic.id}`} aria-hidden="true"></span>;
        }
        return <span className={`clompy-preview-fx clompy-pop-${cosmetic.id}`} aria-hidden="true">{EFFECTS[cosmetic.id] || ''}+1</span>;
    }

    renderShop(diamonds) {
        let { shop, game, buying, shopError, loggedIn } = this.state;
        if (!shop) {
            return <p className="clompy-panel-empty">Loading the shop...</p>;
        }
        let price = (cost) => <><DiamondIcon />{cost.toLocaleString()}</>;
        return (
            <>
                <p className="clompy-shop-balance" role="status" aria-label={`${diamonds.toLocaleString()} diamonds to spend`}><DiamondIcon />{diamonds.toLocaleString()}</p>
                {loggedIn ? null : <p className="clompy-panel-note"><Link to="/login?next=/clompyclicker">Log in</Link> to buy from the shop.</p>}
                {shopError ? <p className="clompy-shop-error" role="alert">{shopError}</p> : null}

                <h3>Upgrades</h3>
                <ul className="clompy-upgrades">
                    {shop.upgrades.map((upgrade) => {
                        let level = game.levels[upgrade.id] || 0;
                        let maxed = level >= upgrade.maxLevel;
                        let cost = maxed ? 0 : upgrade.costs[level];
                        return (
                            <li key={upgrade.id}>
                                <div>
                                    <strong>{upgrade.name}</strong> <span className="clompy-level">Level {level} / {upgrade.maxLevel}</span>
                                    <p>{upgrade.description}</p>
                                </div>
                                <button type="button" className="clompy-button clompy-buy" disabled={maxed || !loggedIn || Boolean(buying) || diamonds < cost}
                                    aria-label={maxed ? `${upgrade.name} is fully upgraded` : `Buy ${upgrade.name} level ${level + 1} for ${cost.toLocaleString()} diamonds`}
                                    onClick={() => this.buy(upgrade.id)}>
                                    {maxed ? 'Maxed' : price(cost)}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {shop.slots.map((slot) => (
                    <React.Fragment key={slot.id}>
                        <h3>{slot.name}</h3>
                        <ul className="clompy-cosmetics">
                            {shop.cosmetics.filter((cosmetic) => cosmetic.slot === slot.id).map((cosmetic) => {
                                let owned = game.owned.includes(cosmetic.id);
                                let wearing = game.equipped[slot.id] === cosmetic.id;
                                return (
                                    <li key={cosmetic.id} className={wearing ? 'is-equipped' : ''}>
                                        {this.renderPreview(cosmetic)}
                                        <span className="clompy-cosmetic-name">{cosmetic.name}</span>
                                        {wearing ?
                                            <button type="button" className="clompy-button clompy-buy" disabled>In use</button>
                                            : owned ?
                                                <button type="button" className="clompy-button clompy-buy" disabled={!loggedIn || Boolean(buying)} aria-label={`Use ${cosmetic.name}`} onClick={() => this.equip(cosmetic.id)}>Use</button>
                                                :
                                                <button type="button" className="clompy-button clompy-buy" disabled={!loggedIn || Boolean(buying) || diamonds < cosmetic.cost}
                                                    aria-label={`Buy ${cosmetic.name} for ${cosmetic.cost.toLocaleString()} diamonds`} onClick={() => this.buy(cosmetic.id)}>
                                                    {price(cosmetic.cost)}
                                                </button>
                                        }
                                    </li>
                                );
                            })}
                        </ul>
                    </React.Fragment>
                ))}
            </>
        );
    }

    render() {
        // while a save is under way its clicks and seconds are in neither count
        let { saved, game, panel, golden, away } = this.state;
        let tier = machineTier(game.levels.machine || 0);
        let towerTier = machineTier(game.levels.barista || 0);
        let banner = this.state.banners[0];
        let allAchievements = (this.state.shop && this.state.shop.achievements) || [];
        let earnedCount = allAchievements.filter((achievement) => this.state.achievements.includes(achievement.id)).length;
        // muted, or the volume turned all the way down
        let silent = this.state.muted || this.state.volume === 0;
        let unsavedClicks = this.state.pending + this.sending;
        let unsavedSeconds = this.state.pendingSeconds + this.sendingSeconds;
        let unsaved = unsavedClicks * game.perClick + unsavedSeconds * game.perSecond;
        let diamonds = saved.coins + unsaved;
        let stats = [
            { label: 'Current diamonds', value: diamonds.toLocaleString(), diamond: true },
            { label: 'All-time diamonds', value: (saved.allTime + unsaved).toLocaleString(), diamond: true },
            { label: 'Diamonds per click', value: game.perClick.toLocaleString(), diamond: true },
            { label: 'Diamonds per second', value: game.perSecond.toLocaleString(), diamond: true },
            { label: 'Diamonds per hour away', value: game.awayPerHour.toLocaleString(), diamond: true },
            { label: 'Longest time away', value: formatAway(game.awayMaxSeconds) },
            { label: 'All-time clicks', value: (saved.clicks + unsavedClicks).toLocaleString() },
            { label: 'Time played', value: formatDuration(saved.seconds + unsavedSeconds) },
            { label: 'Offline collection time', value: formatDuration(saved.awaySeconds) },
            { label: 'Golden diamonds caught', value: saved.goldenCaught.toLocaleString() }
        ];

        let helpPopup = (
            <div className='wordle-popup' onClick={this.toggleHelp.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>How to play</h2>
                    <p>
                        The goal of the game is to collect Diamonds.
                        <br />
                        Diamonds can be collected by clicking the mug.
                        <br />
                        Catch a golden diamond when one appears for a bonus.
                        <br />
                        Set up a Cold Brew Tower from the shop to keep earning while you are away.
                        <br />
                        Spend Diamonds in the shop on upgrades and new looks.
                        <br />
                        Compete against others to gather the most Diamonds.
                    </p>
                </div>
            </div>
        );

        let sharePopup = (
            <div className='wordle-popup' onClick={this.toggleShare.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>Share this game</h2>
                    <div className='button-row'>
                        <a className='wordle-button' href={`https://twitter.com/intent/tweet?text=I%20just%20played%20a%20word%20puzzle%20with%20Wordle%20on%20clomp.io!%20Check%20it%20out%20at%20${encodeURIComponent(this.getShareURL())}`} target='_blank' rel='noopener noreferrer'
                            style={
                                {
                                    backgroundColor: '#1DA1F2',
                                    color: 'white'
                                }
                            }>
                            {/* twitter icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="#FFFFFF" d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path>

                            </svg>
                        </a>
                        <div className='wordle-button' onClick={ () => {
                                navigator.clipboard.writeText(this.getShareURL());
                            }}>
                            Copy link &nbsp;
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="var(--color-tone-1)" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        );
        return (
            <>
                <Navbar />
                <div className="wordle">
                    <div className="wordle-header">
                        <h1>Clompy Clicker</h1>
                        <div onClick={this.toggleHelp.bind(this)}>
                            <svg height="24" viewBox="0 0 24 24" width="24">
                                <path fill="var(--color-tone-1)" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"></path>
                            </svg>
                        </div>
                    </div>

                    <div className="clompyclicker">
                        <div className="game-area">
                            {/* the scene: Clompy on a counter, light fanning out behind, in the background being worn */}
                            <div className={`clompy-stage clompy-${game.equipped.background}`} ref={this.stage}>
                                <div className="clompy-rays" aria-hidden="true"></div>
                                {/* the sound button; pointing at it (or tabbing to it) slides out a volume slider beside it */}
                                <div className="clompy-sound-box">
                                    <input type="range" className="clompy-volume" min="0" max="100" step="5" aria-label="Volume"
                                        value={Math.round(this.state.volume * 100)} onChange={this.volumeChanged}
                                        onMouseUp={this.volumeChosen} onTouchEnd={this.volumeChosen} onKeyUp={this.volumeChosen} />
                                    <button type="button" className="clompy-sound" aria-pressed={silent} onClick={this.toggleSound}
                                        aria-label={this.state.muted ? 'Sound is off: turn it on' : 'Sound is on: turn it off'} title={this.state.muted ? 'Sound off' : 'Sound on'}>
                                        <SoundIcon muted={silent} />
                                    </button>
                                </div>
                                {/* the coffee machine, once one has been bought; a new look pops in afresh */}
                                {tier > 0 ?
                                    <Prop side="left" name="Coffee Machine" level={game.levels.machine} earns={`+${game.perSecond.toLocaleString()}/s`}>
                                        <CoffeeMachine key={tier} tier={tier} className={`clompy-machine clompy-machine-${tier}`} aria-hidden="true" />
                                    </Prop>
                                    : null}
                                {/* and on the other side the cold brew tower, which earns while the player is away */}
                                {towerTier > 0 ?
                                    <Prop side="right" name="Cold Brew Tower" level={game.levels.barista} earns={`+${game.awayPerHour.toLocaleString()}/h`} when="while away">
                                        <ColdBrewTower key={towerTier} tier={towerTier} className={`clompy-machine clompy-machine-${towerTier}`} aria-hidden="true" />
                                    </Prop>
                                    : null}
                                <div className="clompy-diamonds" role="status" aria-label={`${diamonds.toLocaleString()} diamonds`}>
                                    <DiamondIcon />
                                    <span>{diamonds.toLocaleString()}</span>
                                </div>
                                {/* the mug sits in a box of fixed size and squashes inside it when pressed, so nothing around it moves */}
                                <div className="clompy-mug">
                                    <ClompyMug skin={game.equipped.mug} className={`clompy-mug-art${this.state.pressed ? ' is-pressed' : ''}`}
                                        role="button" tabIndex="0" aria-label="Clompy the mug: click to collect diamonds"
                                        onPointerDown={this.clompyClicked} onPointerUp={this.clompyReleased} onPointerLeave={this.clompyReleased} onPointerCancel={this.clompyReleased}
                                        onKeyDown={this.clompyKeyDown} onKeyUp={this.clompyReleased} onBlur={this.clompyReleased} />
                                </div>
                                {/* a newly earned achievement, announced across the top of the scene for a few seconds */}
                                {banner ?
                                    <div className="clompy-banner" key={banner.key} role="status">
                                        <TrophyIcon />
                                        <span>
                                            <span className="clompy-banner-label">Achievement unlocked</span>
                                            <strong>{banner.title}</strong>
                                            {banner.description ? <span className="clompy-banner-text">{banner.description}</span> : null}
                                        </span>
                                    </div>
                                    : null}
                                {golden ?
                                    <button type="button" className="clompy-golden" style={{ left: `${golden.x}%`, top: `${golden.y}%` }}
                                        aria-label="Golden diamond: click it before it gets away" onClick={this.catchGolden}>
                                        <DiamondIcon golden />
                                    </button>
                                    : null}
                                {this.state.pops.map((pop) => (
                                    <span key={pop.id} className={`clompy-pop clompy-pop-${game.equipped.effect}${pop.big ? ' is-big' : ''}`} aria-hidden="true"
                                        style={{ left: `${pop.x}%`, top: `${pop.y}%` }} onAnimationEnd={() => this.popDone(pop.id)}>{pop.text}</span>
                                ))}
                            </div>
                            <div className="clompy-buttons">
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'shop' })}>Shop</button>
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'stats' })}>Stats</button>
                                <Link className="clompy-button clompy-button-primary" to="/leaderboard?game=clompyclicker">Leaderboard</Link>
                            </div>
                            {this.state.loggedIn ? null :
                                <p className="clompy-note"><Link to="/login?next=/clompyclicker">Log in</Link> to save your diamonds, use the shop and join the leaderboard.</p>
                            }
                        </div>
                    </div>

                    {away ?
                        <Panel title="Welcome back!" closeLabel="Collect" onClose={this.closeAway}>
                            <p className="clompy-away">While you were away for {formatAway(away.seconds)}, your Cold Brew Tower collected</p>
                            <p className="clompy-away-earned"><DiamondIcon />{away.earned.toLocaleString()}</p>
                            {away.counted < away.seconds ?
                                <p className="clompy-panel-note">Your Cold Brew Tower drips for up to {formatAway(game.awayMaxSeconds)} at a time. A Thermos from the shop makes that longer.</p>
                                : null}
                        </Panel>
                        : null}
                    {panel === 'shop' ?
                        <Panel title="Shop" wide onClose={this.closePanel}>
                            {this.renderShop(diamonds)}
                        </Panel>
                        : null}
                    {panel === 'stats' ?
                        <Panel title="Stats" wide onClose={this.closePanel}>
                            <dl className="clompy-stats">
                                {stats.map((stat) => (
                                    <div key={stat.label}>
                                        <dt>{stat.label}</dt>
                                        <dd>{stat.diamond ? <DiamondIcon /> : null}{stat.value}</dd>
                                    </div>
                                ))}
                            </dl>
                            {this.state.loggedIn ? null : <p className="clompy-panel-note">These count this visit only. Log in to keep them.</p>}

                            {allAchievements.length > 0 ?
                                <>
                                    <h3>Achievements <span className="clompy-level">{earnedCount} / {allAchievements.length}</span></h3>
                                    {this.state.loggedIn ? null : <p className="clompy-panel-note">Log in to earn achievements.</p>}
                                    <ul className="clompy-achievements">
                                        {allAchievements.map((achievement) => {
                                            let earned = this.state.achievements.includes(achievement.id);
                                            return (
                                                <li key={achievement.id} className={earned ? 'is-earned' : ''}>
                                                    <TrophyIcon />
                                                    <span>
                                                        <strong>{achievement.title}</strong>
                                                        <span className="clompy-achievement-text">{achievement.description}</span>
                                                        <span className="sr-only">{earned ? ' (earned)' : ' (not earned yet)'}</span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                                : null}
                        </Panel>
                        : null}
                    {this.state.showHelp ? helpPopup : null}
                    {this.state.showShare ? sharePopup : null}
                </div>
            </>
        )
    }

}
